import { AuditDeliveryNotice, clearAuditDelivery } from '../lib/auditDelivery';
import { SESSION_ENDED, endSession } from '../lib/sessionFailure';
import axios from 'axios';
import { clearQuestionStudyDrafts } from '../hooks/useQuestionStudyDraft';
import { clearRevisionReflectionDrafts } from '../hooks/useRevisionReflectionDraft';
import { clearProfileNameDrafts } from '../hooks/useProfileNameDraft';
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import api from '@/lib/api';
import { verifyToken } from '@/lib/jwtUtils';

export interface InstitutionSelection {
  selectionToken: string;
  institutions: { tenantId: string; institutionName: string; roles: string[] }[];
}

interface User {
  id: string;
  username: string;
  email: string;
  name?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<InstitutionSelection | void>;
  selectInstitution: (selectionToken: string, tenantId: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateProfile: (name: string) => Promise<void>;
  isAuthenticated: boolean;
}

const publicCredentialRoutes = ['/login', '/register', '/verify-email', '/setup', '/forgot-password', '/reset-password', '/password-reset'];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError,setAuthError]=useState(false);
  const [authAttempt,setAuthAttempt]=useState(0);
  const verifiedToken = useRef<string | null>(null);
  useEffect(() => {
    const changed = (event: StorageEvent) => {
      if (event.key === 'token') { if (!event.newValue) { clearQuestionStudyDrafts(); clearProfileNameDrafts(); clearRevisionReflectionDrafts(); } window.location.assign('/'); }
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const expired = () => {
      verifiedToken.current = null; setUser(null);
      if (!publicCredentialRoutes.includes(location.pathname)) navigate('/login', {replace:true});
    };
    window.addEventListener(SESSION_ENDED, expired);
    return () => window.removeEventListener(SESSION_ENDED, expired);
  }, [navigate, location.pathname]);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    const capturedToken = localStorage.getItem('token');
    setAuthError(false);
    // A profile check for the same session must leave the active page mounted.
    setIsLoading(!!capturedToken && capturedToken !== verifiedToken.current);
    const validateAndSetAuth = async () => {
      console.log('AuthContext: Validating authentication...');
      const token = capturedToken;
      const currentPath = location.pathname;

      if (token) {
        try {
          // Verify token is valid and not expired
          const payload = verifyToken(token);

          if (payload) {
            console.log('AuthContext: Token is valid, fetching user profile');

            // Fetch user profile to ensure user still exists and is active
            const response = await api.get('/auth/me');
            if (cancelled || localStorage.getItem('token') !== capturedToken) return;
            const userData = response.data;
            verifiedToken.current = token;

            setUser({
              id: userData.id || payload.sub || payload.userId,
              username: userData.username || userData.email,
              email: userData.email,
              name: userData.name
            });

            // If we're on the login page, redirect to dashboard
            if (currentPath === '/login') {
              console.log('AuthContext: Already logged in, redirecting to dashboard');
              navigate('/', { replace: true });
            }

            setIsLoading(false);
            return;
          } else {
            console.error('AuthContext: Token is expired or invalid');

            // Clear the invalid token
            verifiedToken.current = null;
            localStorage.removeItem('token');
            setUser(null);

            // If we're not on a public page, redirect to login
            if (!publicCredentialRoutes.includes(currentPath)) {
              console.log('AuthContext: Token invalid, redirecting to login');
              navigate('/login', { replace: true });
            }

            setIsLoading(false);
            return;
          }
        } catch (error) {
          if (cancelled || localStorage.getItem('token') !== capturedToken) return;
          // Only invalidate credentials when the server explicitly rejects them.
          if (axios.isAxiosError(error) && [401,403].includes(error.response?.status || 0)) {
            endSession(token);
            setUser(null);
          } else if (verifiedToken.current !== token) {
            setAuthError(true);
          }
        }
      } else {
        console.log('AuthContext: No token found');
      }

      setIsLoading(false);
    };

    validateAndSetAuth();
    return () => { cancelled = true; };
  }, [location.pathname, navigate, authAttempt]);



  const finishLogin = (loginData: any) => {
      if (!loginData.token || !loginData.roles?.includes('ROLE_STUDENT')) throw new Error('This membership does not have Student portal access.');
      // Extract token and user data from LoginResponse
      const token = loginData.token;
      const user = {
        id: loginData.userId,
        username: loginData.username || loginData.email,
        email: loginData.email,
        name: loginData.name
      };

      localStorage.removeItem('tenantCode');
      localStorage.setItem('token', token);
      verifiedToken.current = token;
      setUser(user);
      toast({
        title: 'Login Successful',
        description: `Welcome back, ${user.name || user.email}!`,
      });
      navigate('/');
  };

  const selectInstitution = async (selectionToken: string, tenantId: string) => {
    const { data } = await api.post('/auth/institutions/select', { selectionToken, tenantId });
    finishLogin(data);
  };

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/login', { email, password });
      const loginData = response.data;

      if (loginData.selectionToken) return loginData as InstitutionSelection;
      finishLogin(loginData);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (username: string, email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/register', { username, email, password });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      verifiedToken.current = token;
      setUser(user);
      toast({
        title: 'Registration Successful',
        description: 'Your account has been created successfully!',
      });
      navigate('/');
    } catch (error) {
      toast({
        title: 'Registration Failed',
        description: 'Could not create your account. Please try again.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (name: string) => {
    const response = await api.put('/auth/profile', { name });
    setUser(current => current ? { ...current, name: response.data.name } : current);
  };

  const logout = async () => {
    if (localStorage.getItem('token')) {
      try { await api.post('/auth/logout'); }
      catch (error: any) {
        if (![401, 403].includes(error?.response?.status)) {
          toast({ title: 'Sign out failed', description: 'Could not revoke your session. Please retry.', variant: 'destructive' });
          return;
        }
      }
    }
    clearProfileNameDrafts();
    clearRevisionReflectionDrafts();
    clearQuestionStudyDrafts();
    clearAuditDelivery();
    verifiedToken.current = null;
    localStorage.removeItem('token');
    setUser(null);
    toast({
      title: 'Logged Out',
      description: 'You have been successfully logged out.',
    });
    navigate('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        selectInstitution,
        register,
        logout,
        updateProfile,
        isAuthenticated: !!user,
      }}
    >
      <AuditDeliveryNotice />{authError && !publicCredentialRoutes.includes(location.pathname)?<main role="alert" className="mx-auto max-w-lg p-8"><p>Unable to verify your session. Please retry when the service is available.</p><button type="button" className="min-h-11 underline" onClick={()=>setAuthAttempt(value=>value+1)}>Retry session</button></main>:children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
